// src/services/api.js
import config from '../config';

class ApiService {
  constructor() {
    this.baseUrl = config.apiBaseUrl;
    // NEW: Cortex engine state (populated from login/user-info response)
    this.queryEngine = 'legacy';        // 'cortex' or 'legacy' — set by App.js after login
    this.cortexThreadId = null;          // persists per chat session
    this.cortexParentMessageId = 0;      // chain: 0 → msgId1 → msgId2...
  }

  async request(endpoint, options = {}) {
    const url = `${this.baseUrl}${endpoint}`;
    const token = localStorage.getItem(config.storage.tokenKey);

    const headers = {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      ...options.headers,
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    // 1 retry for network errors and 502/503/504 only
    const maxRetries = 1;
    let lastError = null;

    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        const response = await fetch(url, {
          ...options,
          headers,
          credentials: 'include',
        });

        if (!response.ok) {
          if (response.status === 401) {
            localStorage.removeItem(config.storage.tokenKey);
            localStorage.removeItem(config.storage.userKey);
            window.location.reload();
          }

          // Retry on 502/503/504 (server temporarily down)
          if ([502, 503, 504].includes(response.status) && attempt < maxRetries) {
            lastError = new Error(`HTTP error! status: ${response.status}`);
            await new Promise(r => setTimeout(r, 2000));
            continue;
          }

          // Try to get error message from response body
          let serverMsg = '';
          try {
            const body = await response.json();
            serverMsg = body.error || body.message || '';
          } catch (_) {}

          throw new Error(serverMsg || `HTTP error! status: ${response.status}`);
        }

        return await response.json();
      } catch (error) {
        // Don't retry client errors (4xx)
        if (error.message && error.message.includes('status: 4')) {
          throw error;
        }

        // Retry on network failure (Failed to fetch = no internet, CORS, DNS, Cloudflare down)
        if (attempt < maxRetries && error instanceof TypeError) {
          lastError = error;
          await new Promise(r => setTimeout(r, 2000));
          continue;
        }

        // Final failure — friendly message
        console.error('API request failed:', error);
        if (error instanceof TypeError && error.message === 'Failed to fetch') {
          throw new Error('Unable to reach the server. Please check your connection and try again.');
        }
        throw error;
      }
    }

    throw lastError || new Error('Request failed. Please try again.');
  }

  // ========================================
  // NEW: CORTEX ENGINE HELPERS
  // ========================================

  /**
   * Set the query engine mode. Called by App.js after login or fetchUserInfo.
   * When switching engine or re-setting, cortex thread state is reset.
   */
  setQueryEngine(engine) {
    this.queryEngine = engine || 'legacy';
    this.cortexThreadId = null;
    this.cortexParentMessageId = 0;
  }

  /**
   * Reset cortex thread state. Called when starting a new conversation
   * or switching to a different conversation.
   * No-op effect when queryEngine is 'legacy'.
   */
  resetCortexThread() {
    this.cortexThreadId = null;
    this.cortexParentMessageId = 0;
  }

  /**
   * ◀── STREAMING: Get current cortex thread state.
   * Used by the streaming path in App.js to read thread/message IDs
   * without going through sendQuery().
   */
  getCortexThreadState() {
    return {
      cortex_thread_id: this.cortexThreadId || '',
      parent_message_id: this.cortexParentMessageId || 0,
    };
  }

  /**
   * ◀── STREAMING: Set cortex thread state from streaming response.
   * After a streaming query completes, App.js calls this to sync
   * the thread state back into apiService so that subsequent
   * non-streaming calls (e.g. voice queries) use the correct IDs.
   */
  setCortexThreadState({ cortex_thread_id, parent_message_id }) {
    if (cortex_thread_id) {
      this.cortexThreadId = cortex_thread_id;
    }
    if (parent_message_id !== undefined) {
      this.cortexParentMessageId = parent_message_id;
    }
  }

  // ========================================
  // CONVERSATION METHODS
  // ========================================

  async getAllConversations() {
    try {
      const response = await this.request('/api/conversations');
      return response.conversations || [];
    } catch (error) {
      console.error('Error fetching conversations:', error);
      return [];
    }
  }

  async getConversation(conversationId) {
    return this.request(`/api/conversations/${conversationId}`);
  }

  async createConversation(title = 'New Conversation') {
    return this.request('/api/conversations', {
      method: 'POST',
      body: JSON.stringify({ title }),
    });
  }

  async updateConversationTitle(conversationId, title) {
    return this.request(`/api/conversations/${conversationId}/title`, {
      method: 'PUT',
      body: JSON.stringify({ title }),
    });
  }

  async deleteConversation(conversationId) {
    return this.request(`/api/conversations/${conversationId}`, {
      method: 'DELETE',
    });
  }

  async sendMessage(conversationId, message) {
    return this.request(`/api/conversations/${conversationId}/messages`, {
      method: 'POST',
      body: JSON.stringify({ message }),
    });
  }

  async getConversationMessages(conversationId) {
    return this.request(`/api/conversations/${conversationId}/messages`);
  }

  // ========================================
  // QUERY METHODS
  // ========================================

  /**
   * Send query to backend
   * This is called by:
   * 1. Text mode - directly from ChatInterface
   * 2. Voice mode - Voice Gateway translates user's speech to English,
   *    sends to your backend via this endpoint, gets response in English,
   *    then translates back to user's language and speaks it
   *
   * When queryEngine === 'cortex':
   *   - Includes cortex_thread_id + parent_message_id in the request
   *   - Captures cortex_thread_id + parent_message_id from the response
   *     for the next query in this conversation
   * When queryEngine === 'legacy':
   *   - Sends the same payload as before (no cortex fields)
   */
  async sendQuery(question, conversationId = null, skillId = null) {
    const body = {
      question,
      conversation_id: conversationId,
    };

    // NEW: Cortex engine — include thread + parent message for conversation context
    if (this.queryEngine === 'cortex') {
      if (this.cortexThreadId) {
        body.cortex_thread_id = this.cortexThreadId;
      }
      if (this.cortexParentMessageId !== undefined) {
        body.parent_message_id = this.cortexParentMessageId;
      }
      // SKILLS: include selected skill_id for injection on first message
      if (skillId) {
        body.skill_id = skillId;
      }
    }

    const result = await this.request('/api/query', {
      method: 'POST',
      body: JSON.stringify(body),
    });

    // NEW: Cortex engine — capture thread + message IDs from response for next call
    if (result.query_engine === 'cortex') {
      if (result.cortex_thread_id) {
        this.cortexThreadId = result.cortex_thread_id;
      }
      if (result.parent_message_id !== undefined) {
        this.cortexParentMessageId = result.parent_message_id;
      }
    }

    return result;
  }

  // ========================================
  // THREAD LIMITS
  // ========================================

  async getThreadLimits() {
    try {
      return await this.request('/api/thread-limits');
    } catch (error) {
      console.warn('Thread limits not available, using defaults:', error.message);
      return { warn_at: 999, max_messages: 999 };
    }
  }

  // ========================================
  // USER METHODS
  // ========================================

  async getUserInfo() {
    return this.request('/api/user-info');
  }

  // USER MANAGEMENT - Match backend endpoints
  async getUsers() {
    return this.request('/api/users/list');
  }

  async createUser(userData) {
    return this.request('/api/users/create', {
      method: 'POST',
      body: JSON.stringify(userData),
    });
  }

  async updateUser(username, userData) {
    return this.request(`/api/users/${username}`, {
      method: 'PUT',
      body: JSON.stringify(userData),
    });
  }

  async deleteUser(username) {
    return this.request(`/api/users/${username}`, {
      method: 'DELETE',
    });
  }

  async searchUsers(searchTerm) {
    return this.request(`/api/users/search?q=${encodeURIComponent(searchTerm)}`);
  }

  async getUserStats() {
    return this.request('/api/users/stats');
  }

  // ========================================
  // LOCATION METHODS
  // ========================================

  async getLocations() {
    try {
      const response = await this.request('/api/users/locations');
      return response;
    } catch (error) {
      console.error('Error fetching locations:', error);
      return { success: false, locations: [], count: 0 };
    }
  }

  async getUserLocations(username) {
    try {
      const response = await this.request(`/api/users/${encodeURIComponent(username)}/locations`);
      return response;
    } catch (error) {
      console.error('Error fetching user locations:', error);
      return { success: false, username, locations: [] };
    }
  }

  async updateUserLocations(username, locations) {
    try {
      const response = await this.request(`/api/users/${encodeURIComponent(username)}/locations`, {
        method: 'PUT',
        body: JSON.stringify({
          locations: Array.isArray(locations) ? locations : []
        }),
      });
      return response;
    } catch (error) {
      console.error('Error updating user locations:', error);
      throw error;
    }
  }

  // ========================================
  // PREDEFINED QUESTIONS
  // ========================================

  async getPredefinedQuestions() {
    try {
      const response = await this.request('/api/predefined-questions');
      return response;
    } catch (error) {
      console.error('Error fetching predefined questions:', error);
      return { questions: [] };
    }
  }

  // ========================================
  // BULK OPERATIONS
  // ========================================

  async bulkCreateUsers(usersData) {
    return this.request('/api/users/bulk-create', {
      method: 'POST',
      body: JSON.stringify({ users: usersData }),
    });
  }
}

// Create and export a singleton instance
const apiService = new ApiService();

export default apiService;
export { apiService };
